// src/services/payment-calculator.service.ts
import { PAYMENT_CONFIG } from "../config/payment.config";
import { whitelistService } from "./whitelist.service";

export interface PaymentBreakdown {
  basePlatformFee: number;
  totalPlatformFee: number;
  bouquetAmount: number;
  extraContribution: number;
  vat: number;
  total: number;
  petals: number;
  discountPercentage: number;
  originalTotal: number;
}

class PaymentCalculator {
  private readonly config = PAYMENT_CONFIG;

  /**
   * Calculate payment breakdown with whitelist discount support
   */
  async calculatePaymentBreakdownWithDiscount(
    bouquetAmount: number,
    extraContribution: number = 0,
    userEmail?: string | null
  ): Promise<PaymentBreakdown> {
    console.log("💰 [PaymentCalculator] Calculating breakdown with discount:", {
      bouquetAmount,
      extraContribution,
      userEmail,
      timestamp: new Date().toISOString(),
    });

    // Check whitelist status
    let discountPercentage = 0;
    if (userEmail) {
      console.log("💰 [PaymentCalculator] Checking whitelist for email:", userEmail);
      const whitelistStatus = await whitelistService.checkEmail(userEmail);
      discountPercentage = whitelistStatus.discountPercentage;

      console.log("💰 [PaymentCalculator] Whitelist result:", {
        email: userEmail,
        isWhitelisted: whitelistStatus.isWhitelisted,
        discountPercentage,
        hasEntry: !!whitelistStatus.entry,
      });

      // Track usage if whitelisted (fire and forget)
      if (whitelistStatus.isWhitelisted) {
        console.log("💰 [PaymentCalculator] Incrementing usage for whitelisted email");
        whitelistService.incrementUsage(userEmail).catch(console.error);
      }
    } else {
      console.log("💰 [PaymentCalculator] No user email provided for whitelist check");
    }

    // Calculate original amounts
    const originalTotalPlatformFee = Math.max(
      this.config.basePlatformFee,
      bouquetAmount * 0.1 // 10% of bouquet amount
    );

    const originalVat =
      (bouquetAmount + originalTotalPlatformFee + extraContribution) * this.config.vatRate;
    const originalTotal =
      bouquetAmount + originalTotalPlatformFee + extraContribution + originalVat;

    console.log("💰 [PaymentCalculator] Original amounts:", {
      originalTotalPlatformFee,
      originalVat,
      originalTotal,
      discountPercentage,
    });

    // Apply discount if applicable
    if (discountPercentage > 0) {
      console.log("💰 [PaymentCalculator] Applying discount:", discountPercentage + "%");

      // For 100% discount, everything becomes 0
      if (discountPercentage >= 100) {
        console.log("🎉 [PaymentCalculator] 100% discount applied! Returning zero amounts");
        const result = {
          basePlatformFee: this.config.basePlatformFee,
          totalPlatformFee: 0,
          bouquetAmount: 0,
          extraContribution: 0,
          vat: 0,
          total: 0,
          petals: 0,
          discountPercentage: 100,
          originalTotal,
        };
        console.log("💰 [PaymentCalculator] Free breakdown result:", result);
        return result;
      }

      // Apply partial discount
      const discountMultiplier = (100 - discountPercentage) / 100;

      const result = {
        basePlatformFee: this.config.basePlatformFee,
        totalPlatformFee: Math.floor(originalTotalPlatformFee * discountMultiplier),
        bouquetAmount: Math.floor(bouquetAmount * discountMultiplier),
        extraContribution: Math.floor(extraContribution * discountMultiplier),
        vat: Math.floor(originalVat * discountMultiplier),
        total: Math.floor(originalTotal * discountMultiplier),
        petals: Math.floor((bouquetAmount * discountMultiplier) / this.config.petalRate),
        discountPercentage,
        originalTotal: Math.floor(originalTotal),
      };

      console.log("💰 [PaymentCalculator] Discounted breakdown result:", result);
      return result;
    }

    // No discount - truncate all values to integers
    const result = {
      basePlatformFee: this.config.basePlatformFee,
      totalPlatformFee: Math.floor(originalTotalPlatformFee),
      bouquetAmount: Math.floor(bouquetAmount),
      extraContribution: Math.floor(extraContribution),
      vat: Math.floor(originalVat),
      total: Math.floor(originalTotal),
      petals: Math.floor(bouquetAmount / this.config.petalRate),
      discountPercentage: 0,
      originalTotal: Math.floor(originalTotal),
    };

    console.log("💰 [PaymentCalculator] No discount applied, regular breakdown:", result);
    return result;
  }

  // Keep original synchronous method for backward compatibility
  calculatePaymentBreakdown(
    bouquetAmount: number,
    extraContribution: number = 0
  ): Omit<PaymentBreakdown, "discountPercentage" | "originalTotal"> {
    const totalPlatformFee = Math.max(this.config.basePlatformFee, bouquetAmount * 0.1);
    const vat = (bouquetAmount + totalPlatformFee + extraContribution) * this.config.vatRate;
    const total = bouquetAmount + totalPlatformFee + extraContribution + vat;

    return {
      basePlatformFee: this.config.basePlatformFee,
      totalPlatformFee: Math.floor(totalPlatformFee),
      bouquetAmount: Math.floor(bouquetAmount),
      extraContribution: Math.floor(extraContribution),
      vat: Math.floor(vat),
      total: Math.floor(total),
      petals: Math.floor(bouquetAmount / this.config.petalRate),
    };
  }

  formatPrice(amount: number): string {
    if (amount === 0) return "Free";

    // Truncate to 0 decimals and format with commas
    const truncatedAmount = Math.floor(amount);

    // Format with commas for thousands
    const formattedAmount = truncatedAmount.toLocaleString("en-US", {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });

    return `${this.config.currency} ${formattedAmount}`;
  }

  validateBouquetAmount(amount: number): boolean {
    // Allow 0 as a valid amount (no bouquet)
    if (amount === 0) return true;
    return amount >= this.config.minBouquetAmount && amount <= this.config.maxBouquetAmount;
  }

  validateExtraContribution(amount: number): boolean {
    return amount >= 0 && amount <= this.config.maxExtraContribution;
  }
}

export const paymentCalculator = new PaymentCalculator();
